import { parseHpxmlDocument } from "./hpxmlDom";
import { extractHpxmlEnclosureAudit, extractHpxmlSystemsAudit } from "./hpxmlAuditExtract";
import { buildVoltaEnvelopeAuditRows } from "./voltaEnvelopeAudit";
import { buildVoltaSystemsAudit } from "./voltaSystemsAudit";
import { makeAuditError, logAuditFailure } from "./auditDiagnostics";
import { formatEnvelopeRAreaLines } from "./envelopeAuditFormat";

function numbersClose(a, b, { abs = 0.5, rel = 0.04 } = {}) {
    if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) return null;
    const tol = Math.max(abs, Math.abs(b) * rel);
    return Math.abs(a - b) <= tol;
}

function formatM2(v) {
    if (v == null || !Number.isFinite(v)) return "—";
    return `${v.toFixed(2)} m²`;
}

function formatAirInfiltration(ai) {
    if (!ai) return "—";
    const parts = [];
    if (ai.airLeakage != null) parts.push(`${ai.airLeakage.toFixed(1)} ACH`.trim());
    if (ai.specificLeakageArea != null) parts.push(`SLA ${ai.specificLeakageArea}`);
    if (ai.effectiveLeakageArea != null) parts.push(`ELA ${ai.effectiveLeakageArea}`);
    return parts.length ? parts.join("; ") : "—";
}

/** Surfaces the translator synthesizes with no H2K counterpart, listed after `after`. */
const HPXML_ONLY_ROWS = [
    { id: "hpxmlGableWall", label: "Gable End Walls (HPXML only)", after: "wall" },
    { id: "hpxmlAttachedWall", label: "Attached (HPXML only)", after: "wall" },
];

function buildHpxmlOnlyRow({ id, label }, hpxmlEnclosure) {
    const h = hpxmlEnclosure[id];
    if (!h?.count) return null;

    return {
        id,
        label,
        voltaAreaM2: null,
        voltaSummary: "—",
        hpxmlAreaM2: h.totalM2,
        hpxmlRLabel: formatEnvelopeRAreaLines(h.details || []),
        hpxmlSurfaceCount: h.count,
        areaMatch: null,
    };
}

function emptyReportBase() {
    return {
        parseError: null,
        auditBuildError: null,
        enclosureError: null,
        envelopeRows: [],
        systems: null,
        formatters: { formatM2 },
    };
}

/**
 * @returns {{ parseError: string|null, auditBuildError: object|null, envelopeRows: object[], systems: object|null, enclosureError: string|null }}
 */
export function buildHpxmlAuditReport(hpxmlString, modelData = {}, upgrades = {}, upgradePackageId = "") {
    const report = emptyReportBase();

    if (!!upgradePackageId && !Object.keys(upgrades).includes(upgradePackageId)) {
        report.upgradePackageError = "UPGRADE_PACKAGE_NOT_FOUND";
    }

    const { doc, error } = parseHpxmlDocument(hpxmlString);
    if (error || !doc) {
        report.parseError = error || "Parse failed.";
        return report;
    }

    let hpxmlEnclosure;
    let airInfiltration;
    try {
        const enc = extractHpxmlEnclosureAudit(doc);
        hpxmlEnclosure = enc.rows;
        airInfiltration = enc.airInfiltration;
        report.enclosureError = enc.error;
    } catch (err) {
        report.auditBuildError = makeAuditError(err, { phase: "hpxmlEnclosureExtract" });
        logAuditFailure(report.auditBuildError);
        return report;
    }

    let voltaEnvelope;
    try {
        voltaEnvelope = buildVoltaEnvelopeAuditRows(modelData, upgrades, upgradePackageId);
    } catch (err) {
        report.auditBuildError = makeAuditError(err, {});
        logAuditFailure(report.auditBuildError);
        return report;
    }

    let hpxmlSystems;
    try {
        hpxmlSystems = extractHpxmlSystemsAudit(doc);
    } catch (err) {
        report.auditBuildError = makeAuditError(err, { phase: "hpxmlSystemsExtract" });
        logAuditFailure(report.auditBuildError);
        return report;
    }

    let voltaSystems;
    try {
        voltaSystems = buildVoltaSystemsAudit(modelData, upgrades, upgradePackageId);
    } catch (err) {
        report.auditBuildError = makeAuditError(err, {});
        logAuditFailure(report.auditBuildError);
        return report;
    }

    try {
        report.envelopeRows = voltaEnvelope.flatMap((v) => {
            const h = hpxmlEnclosure[v.id];

            // Pony walls are optional in H2K; hide the row entirely when neither side has one.
            if (v.id === "ponyWall" && !v.voltaAreaM2 && !h?.count) return [];
            const extraRows = HPXML_ONLY_ROWS.filter((r) => r.after === v.id)
                .map((r) => buildHpxmlOnlyRow(r, hpxmlEnclosure))
                .filter(Boolean);

            if (v.id === "airTightness") {
                return [
                    {
                        ...v,
                        hpxmlAreaM2: null,
                        hpxmlRLabel: formatAirInfiltration(airInfiltration),
                        areaMatch: null,
                    },
                    ...extraRows,
                ];
            }

            const hArea = h?.totalM2;
            const areaMatch = numbersClose(v.voltaAreaM2, hArea, { abs: 0.75, rel: 0.05 });

            return [
                {
                    ...v,
                    hpxmlAreaM2: hArea,
                    hpxmlRLabel: formatEnvelopeRAreaLines(h?.details || []),
                    hpxmlSurfaceCount: h?.count ?? 0,
                    areaMatch,
                },
                ...extraRows,
            ];
        });
        report.systems = { volta: voltaSystems, hpxml: hpxmlSystems };
    } catch (err) {
        report.auditBuildError = makeAuditError(err, { phase: "hpxmlAuditMerge" });
        logAuditFailure(report.auditBuildError);
        return report;
    }

    return report;
}

export { formatM2, formatAirInfiltration };
export { formatEnvelopeRAreaLines } from "./envelopeAuditFormat";
